<template>
  <PageHeaderLayout>
    <div
      class="main-page-content auth-nlg-page"
      v-loading="loading"
      element-loading-text="加载中..."
    >
      <div class="page-header">
        <div>
          <div class="page-title">企业认证</div>
          <div class="page-desc">
            当前页面展示企业认证状态，并按现有接口发起实名认证、印章授权。
          </div>
        </div>
        <el-buton type="primary" plain @click="fetchStatus">刷新状态</el-buton>
      </div>

      <el-alert
        :title="summaryText"
        type="info"
        :closable="false"
        show-icon
        class="summary-alert"
      />

      <el-row :gutter="16">
        <el-col :span="12">
          <el-card shadow="never" class="auth-card">
            <div slot="header" class="card-header">
              <span>认证概览</span>
              <el-tag :type="overallStatusTag.type" size="mini">
                {{ overallStatusTag.text }}
              </el-tag>
            </div>

            <el-descriptions :column="1" border size="small">
              <el-descriptions-item label="企业名称">
                {{ companyName }}
              </el-descriptions-item>
              <el-descriptions-item label="认证类型">
                非灵工认证
              </el-descriptions-item>
              <el-descriptions-item label="当前状态">
                {{ overallStatusTag.text }}
              </el-descriptions-item>
              <el-descriptions-item label="更新时间">
                {{ updateTime }}
              </el-descriptions-item>
            </el-descriptions>
          </el-card>
        </el-col>

        <el-col :span="12">
          <el-card shadow="never" class="auth-card">
            <div slot="header" class="card-header">
              <span>实名认证</span>
              <el-tag :type="realAuthTag.type" size="mini">
                {{ realAuthTag.text }}
              </el-tag>
            </div>

            <div class="action-block">
              <div class="action-text">
                法人实名认证为后续企业认证的基础步骤，状态以服务端返回结果为准。
              </div>
              <el-button
                type="primary"
                :loading="submitLoading.real"
                @click="handleRealAuth"
              >
                发起实名认证
              </el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="24">
          <el-card shadow="never" class="auth-card">
            <div slot="header" class="card-header">
              <span>印章授权</span>
              <el-tag :type="sealAuthTag.type" size="mini">
                {{ sealAuthTag.text }}
              </el-tag>
            </div>

            <el-form
              ref="sealForm"
              :model="sealForm"
              :rules="sealRules"
              label-width="100px"
              class="seal-form"
            >
              <el-form-item label="选择印章" prop="seal_id">
                <el-select
                  v-model="sealForm.seal_id"
                  placeholder="请选择印章"
                  clearable
                  filterable
                >
                  <el-option
                    v-for="item in sealOptions"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>
            </el-form>

            <div class="action-block action-block-inline">
              <div class="action-text">
                若服务端已返回印章列表，可直接选择对应印章完成授权。
              </div>
              <div>
                <el-button plain @click="fetchSealList">刷新印章</el-button>
                <el-button
                  type="primary"
                  :loading="submitLoading.seal"
                  @click="handleSealAuth"
                >
                  提交印章授权
                </el-button>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <el-card shadow="never" class="auth-card">
        <div slot="header" class="card-header">
          <span>服务端状态</span>
        </div>

        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="实名认证状态">
            {{ realAuthTag.text }}
          </el-descriptions-item>
          <el-descriptions-item label="印章授权状态">
            {{ sealAuthTag.text }}
          </el-descriptions-item>
          <el-descriptions-item label="印章数量">
            {{ sealOptions.length }}
          </el-descriptions-item>
          <el-descriptions-item label="状态说明">
            {{ statusRemark }}
          </el-descriptions-item>
        </el-descriptions>
      </el-card>
    </div>
  </PageHeaderLayout>
</template>

<script>
import PageHeaderLayout from "@/layouts/PageHeaderLayout";

const DEFAULT_STATUS = "未返回";

export default {
  name: "AuthNlg",
  components: {
    PageHeaderLayout,
  },
  data() {
    return {
      loading: false,
      statusData: {},
      sealOptions: [],
      sealForm: {
        seal_id: "",
      },
      sealRules: {
        seal_id: [{ required: true, message: "请选择印章", trigger: "change" }],
      },
      submitLoading: {
        real: false,
        seal: false,
      },
    };
  },
  computed: {
    companyName() {
      return (
        this.pickValue([
          "company_name",
          "corp_name",
          "name",
          "company.name",
          "company_info.name",
        ]) || "-"
      );
    },
    updateTime() {
      return (
        this.pickValue([
          "updated_at",
          "update_time",
          "modified_at",
          "auth.updated_at",
        ]) || "-"
      );
    },
    statusRemark() {
      return (
        this.pickValue([
          "remark",
          "message",
          "status_message",
          "status_desc",
          "auth.remark",
        ]) || "以服务端返回状态为准"
      );
    },
    summaryText() {
      return `当前企业：${this.companyName}；总状态：${this.overallStatusTag.text}；实名认证：${this.realAuthTag.text}；印章授权：${this.sealAuthTag.text}`;
    },
    overallStatusTag() {
      return this.createStatusTag(
        this.pickValue([
          "nlg_auth_status_text",
          "auth_status_text",
          "status_text",
          "status_desc",
          "auth.status_text",
        ]) || DEFAULT_STATUS
      );
    },
    realAuthTag() {
      return this.createStatusTag(
        this.pickValue([
          "real_auth_status_text",
          "real_status_text",
          "real_auth.text",
          "real_auth.status_text",
          "real.status_text",
        ]) || DEFAULT_STATUS
      );
    },
    sealAuthTag() {
      return this.createStatusTag(
        this.pickValue([
          "seal_auth_status_text",
          "seal_status_text",
          "seal_auth.text",
          "seal_auth.status_text",
          "seal.status_text",
        ]) || DEFAULT_STATUS
      );
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      await this.fetchStatus();
      await this.fetchSealList();
    },
    getValueByPath(source, path) {
      return path.split(".").reduce((result, key) => {
        if (result === null || typeof result === "undefined") {
          return undefined;
        }
        return result[key];
      }, source);
    },
    pickValue(paths) {
      for (let i = 0; i < paths.length; i += 1) {
        const value = this.getValueByPath(this.statusData, paths[i]);
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
      return "";
    },
    createStatusTag(text) {
      const value = String(text || DEFAULT_STATUS);
      if (/成功|完成|通过|已认证|已授权/.test(value)) {
        return { text: value, type: "success" };
      }
      if (/审核中|进行中|待处理|待授权|待认证|申请中/.test(value)) {
        return { text: value, type: "warning" };
      }
      if (/失败|驳回|拒绝|异常|未通过/.test(value)) {
        return { text: value, type: "danger" };
      }
      return { text: value, type: "info" };
    },
    normalizeSealList(result) {
      if (Array.isArray(result)) {
        return result;
      }
      if (Array.isArray(result?.list)) {
        return result.list;
      }
      if (Array.isArray(result?.rows)) {
        return result.rows;
      }
      return [];
    },
    async fetchStatus() {
      this.loading = true;
      try {
        const result = await this.$api.getAuthStatus();
        this.statusData = result || {};
      } finally {
        this.loading = false;
      }
    },
    async fetchSealList() {
      try {
        const result = await this.$api.getSealList();
        const list = this.normalizeSealList(result);
        this.sealOptions = list.map((item, index) => ({
          id: item.id || item.seal_id || item.value || index + 1,
          name:
            item.name ||
            item.seal_name ||
            item.label ||
            `印章${index + 1}`,
        }));
        if (!this.sealForm.seal_id && this.sealOptions.length === 1) {
          this.sealForm.seal_id = this.sealOptions[0].id;
        }
      } catch (error) {
        this.sealOptions = [];
      }
    },
    async handleRealAuth() {
      try {
        await this.$confirm("确认发起实名认证授权吗？", "提示", {
          type: "warning",
        });
      } catch (error) {
        return;
      }

      this.submitLoading.real = true;
      try {
        await this.$api.postRealAuth({});
        this.$message.success("实名认证已提交");
        await this.fetchStatus();
      } finally {
        this.submitLoading.real = false;
      }
    },
    handleSealAuth() {
      this.$refs.sealForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        try {
          await this.$confirm("确认提交当前印章授权吗？", "提示", {
            type: "warning",
          });
        } catch (error) {
          return false;
        }

        this.submitLoading.seal = true;
        try {
          await this.$api.postSealAuth({
            seal_id: this.sealForm.seal_id,
          });
          this.$message.success("印章授权已提交");
          await this.fetchStatus();
          await this.fetchSealList();
        } finally {
          this.submitLoading.seal = false;
        }
        return true;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-nlg-page {
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.page-desc {
  margin-top: 8px;
  color: #909399;
  line-height: 20px;
}

.summary-alert {
  margin-bottom: 16px;
}

.auth-card {
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.action-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 72px;
}

.action-block-inline {
  margin-top: 8px;
}

.action-text {
  color: #606266;
  line-height: 22px;
  padding-right: 16px;
}

.seal-form {
  max-width: 520px;
}
</style>
